import { useState } from 'react';
import { useMail } from '../../context/MailContext';
import { format, isToday, isYesterday } from 'date-fns';
import './EmailList.css';

const AVATAR_COLORS = {
  teal: { bg: '#E1F5EE', text: '#0F6E56' },
  blue: { bg: '#E6F1FB', text: '#0C447C' },
  amber: { bg: '#FAEEDA', text: '#633806' },
  pink: { bg: '#FBEAF0', text: '#4B1528' },
  purple: { bg: '#EEEDFE', text: '#26215C' },
  green: { bg: '#EAF3DE', text: '#173404' },
};

const FOLDER_TITLES = {
  inbox: 'Inbox', sent: 'Sent', scheduled: 'Scheduled',
  drafts: 'Drafts', starred: 'Starred', trash: 'Trash',
  autoresponder: 'Auto-Responders', signature: 'Signature', settings: 'Settings',
};

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  if (isToday(d)) return format(d, 'h:mm a');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'MMM d');
}

export default function EmailList() {
  const { activeFolder, folderEmails, activeEmail, openEmail, emails,
          loadingFolder, fetchError, refreshFolder } = useMail();
  const [filter, setFilter] = useState('all');

  const isSettingsPane = ['autoresponder', 'signature', 'settings'].includes(activeFolder);
  const listData = isSettingsPane ? [] : folderEmails(activeFolder);

  const filtered = listData.filter(e => {
    if (filter === 'unread') return e.unread;
    if (filter === 'starred') return e.starred;
    if (filter === 'attachments') return e.attachments?.length > 0;
    return true;
  });

  return (
    <section className="email-list">
      <div className="list-header">
        <h2 className="list-title">{FOLDER_TITLES[activeFolder] || activeFolder}</h2>
        <div className="list-actions">
          <button
            className={`list-icon-btn ${loadingFolder ? 'spinning' : ''}`}
            title="Refresh"
            onClick={() => refreshFolder(activeFolder)}
          >↻</button>
        </div>
      </div>

      {fetchError && (
        <div className="fetch-error">
          ⚠ {fetchError}
          <button onClick={() => refreshFolder(activeFolder)}>Retry</button>
        </div>
      )}

      {!isSettingsPane && (
        <div className="filter-chips">
          {['all', 'unread', 'starred', 'attachments'].map(f => (
            <button
              key={f}
              className={`filter-chip ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div className="email-items-list">
        {loadingFolder && listData.length === 0 ? (
          <div className="loading-list">
            <div className="loading-spinner"></div>
            <p>Loading emails…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-list">
            <span className="empty-icon">
              {activeFolder === 'trash' ? '🗑' :
               activeFolder === 'scheduled' ? '🕐' :
               activeFolder === 'drafts' ? '📝' : '📭'}
            </span>
            <p>No emails here</p>
          </div>
        ) : (
          filtered.map(email => (
            <EmailItem
              key={email.id}
              email={email}
              isActive={activeEmail?.id === email.id}
              onClick={() => openEmail(email)}
            />
          ))
        )}
      </div>
    </section>
  );
}

function EmailItem({ email, isActive, onClick }) {
  const colors = AVATAR_COLORS[email.from?.color] || AVATAR_COLORS.blue;

  return (
    <div
      className={`email-item ${email.unread ? 'unread' : ''} ${isActive ? 'active' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
    >
      {email.unread && <span className="unread-dot" aria-label="Unread"></span>}

      <div className="ei-top">
        <div className="ei-avatar" style={{ background: colors.bg, color: colors.text }}>
          {email.from?.initials || '??'}
        </div>
        <div className="ei-sender">{email.from?.name || email.from?.email || 'Unknown'}</div>
        <div className="ei-time">{formatDate(email.date)}</div>
      </div>

      <div className="ei-subject">{email.subject || '(No subject)'}</div>
      <div className="ei-preview">{email.preview}</div>

      <div className="ei-meta">
        {email.attachments?.length > 0 && (
          <span className="ei-tag attach">📎 {email.attachments.length}</span>
        )}
        {email.readReceipt && email.readAt && (
          <span className="ei-tag receipt">✓ Read</span>
        )}
        {email.folder === 'scheduled' && (
          <span className="ei-tag sched">Scheduled</span>
        )}
        {email.autoReply && <span className="ei-tag auto">Auto</span>}
        {email.starred && <span className="ei-star">⭐</span>}
        {email.labels?.map(l => (
          <span key={l} className="ei-tag label">{l}</span>
        ))}
      </div>
    </div>
  );
}
